import React from 'react'

import { useOrganizationProducts } from './useOrganizationProducts'
import { useOrganizationSubscriptions } from './useOrganizationSubscriptions'

type Product = 'LiveChat' | 'ChatBot' | 'HelpDesk' | 'KnowledgeBase'

type ProductState = 'active' | 'inactive' | 'expired' | 'unknown'

export function useTextProductState(product: Product): ProductState {
	const { organizationProducts, areOrganizationProductsLoading, organizationProductsError } = useOrganizationProducts()
	const { organizationSubscriptions, areOrganizationSubscriptionsLoading, organizationSubscriptionsError } =
		useOrganizationSubscriptions()

	const productState = React.useMemo((): ProductState => {
		if (
			areOrganizationProductsLoading ||
			areOrganizationSubscriptionsLoading ||
			organizationProductsError ||
			organizationSubscriptionsError
		) {
			return 'unknown'
		}

		const hasProduct = organizationProducts?.some((data) => data.product === product)
		if (
			!organizationProducts ||
			!organizationSubscriptions ||
			!organizationSubscriptions.subscriptions ||
			!hasProduct
		) {
			return 'inactive'
		}

		const subscription = organizationSubscriptions.subscriptions[product.toLowerCase() as Lowercase<Product>]
		if (!subscription) {
			return 'inactive'
		}

		if (subscription.status === 'expired') {
			return 'expired'
		}

		return 'active'
	}, [
		product,
		organizationProducts,
		organizationSubscriptions,
		areOrganizationProductsLoading,
		areOrganizationSubscriptionsLoading,
		organizationProductsError,
		organizationSubscriptionsError,
	])

	return productState
}
